import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useStaff } from "@/context/StaffAuth";
import { api, errMsg } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { ErrorState, Field, KeyValue, PageHeader, SectionCard, StatusBadge, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";

export default function AdminSettings() {
  const { can } = useStaff();
  const [data, setData] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [error, setError] = useState(null);
  const [store, setStore] = useState({ store_name: "", support_email: "", default_reorder_point: 2, low_stock_display_threshold: 3 });
  const [fin, setFin] = useState({ costing_policy: "", legal_entity_country: "" });
  const [busy, setBusy] = useState(false);
  const [editInt, setEditInt] = useState(null);
  const [intForm, setIntForm] = useState({ provider: "", notes: "" });

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.get("/admin/settings");
      setData(r.data);
      const s = r.data.settings || {};
      setStore({ store_name: s.store_name || "", support_email: s.support_email || "", default_reorder_point: s.default_reorder_point ?? 2, low_stock_display_threshold: s.low_stock_display_threshold ?? 3 });
      setFin({ costing_policy: s.costing_policy || "", legal_entity_country: s.legal_entity_country || "" });
      if (can("integrations.read")) {
        const i = await api.get("/admin/integrations");
        setIntegrations(i.data);
      }
    } catch (e) {
      setError(errMsg(e));
    }
  }, [can]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = "Settings · Admin"; }, []);

  const saveStore = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", { ...store, default_reorder_point: Number(store.default_reorder_point), low_stock_display_threshold: Number(store.low_stock_display_threshold) });
      toast.success("Store settings saved");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const saveFin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings/finance", { costing_policy: fin.costing_policy || null, legal_entity_country: fin.legal_entity_country || null });
      toast.success("Finance settings saved");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const saveInt = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/integrations/${editInt.key}`, { mode: "NOT_CONFIGURED", provider: intForm.provider || null, notes: intForm.notes });
      toast.success("Integration notes saved");
      setEditInt(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <div className="space-y-6"><Skeleton className="h-12 w-64" /><Skeleton className="h-72 w-full" /></div>;

  const canStore = can("settings.manage");
  const canFin = can("finance.settings");
  const canInt = can("integrations.manage");

  return (
    <div data-testid="admin-settings-page">
      <PageHeader eyebrow="Control" title="Settings" description="Store configuration, financial policy and the integration register. Nothing goes live without verified credentials." />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="Store" description="Public identity and stock thresholds used across admin and storefront." testId="settings-store-card">
            <form onSubmit={saveStore} className="grid sm:grid-cols-2 gap-4">
              <Field label="Store name" htmlFor="st-name" required><Input id="st-name" required value={store.store_name} onChange={(e) => setStore({ ...store, store_name: e.target.value })} disabled={!canStore} data-testid="settings-store-name-input" /></Field>
              <Field label="Support email" htmlFor="st-email"><Input id="st-email" type="email" value={store.support_email} onChange={(e) => setStore({ ...store, support_email: e.target.value })} disabled={!canStore} data-testid="settings-support-email-input" /></Field>
              <Field label="Default reorder point" htmlFor="st-rp" hint="Used when a variant has no explicit reorder point"><Input id="st-rp" type="number" min={0} max={1000} value={store.default_reorder_point} onChange={(e) => setStore({ ...store, default_reorder_point: e.target.value })} disabled={!canStore} data-testid="settings-reorder-input" /></Field>
              <Field label="Storefront low-stock message threshold" htmlFor="st-low" hint="Show ‘Only N left’ at or below this quantity"><Input id="st-low" type="number" min={0} max={50} value={store.low_stock_display_threshold} onChange={(e) => setStore({ ...store, low_stock_display_threshold: e.target.value })} disabled={!canStore} data-testid="settings-lowstock-input" /></Field>
              {canStore && <div className="sm:col-span-2 flex justify-end"><Button type="submit" disabled={busy} data-testid="settings-store-save-button"><Save className="h-4 w-4 mr-1.5" />Save store settings</Button></div>}
            </form>
          </SectionCard>

          <SectionCard title="Finance" description="Costing policy drives inventory valuation (Phase 7). Until set, valuations are labelled as estimates." testId="settings-finance-card">
            <form onSubmit={saveFin} className="grid sm:grid-cols-2 gap-4">
              <Field label="Costing policy" htmlFor="fin-pol">
                <Select value={fin.costing_policy || "__none__"} onValueChange={(v) => setFin({ ...fin, costing_policy: v === "__none__" ? "" : v })} disabled={!canFin}>
                  <SelectTrigger id="fin-pol" data-testid="settings-costing-select"><SelectValue placeholder="Not configured" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Not configured</SelectItem><SelectItem value="weighted_average">Weighted average</SelectItem><SelectItem value="fifo">FIFO</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Legal entity country (ISO-2)" htmlFor="fin-cc"><Input id="fin-cc" maxLength={2} value={fin.legal_entity_country} onChange={(e) => setFin({ ...fin, legal_entity_country: e.target.value.toUpperCase() })} disabled={!canFin} placeholder="IT" className="uppercase" data-testid="settings-country-input" /></Field>
              {canFin && <div className="sm:col-span-2 flex justify-end"><Button type="submit" disabled={busy} data-testid="settings-finance-save-button"><Save className="h-4 w-4 mr-1.5" />Save finance settings</Button></div>}
            </form>
          </SectionCard>

          {integrations && (
            <SectionCard title="Integration register" description="Payments, shipping, tax, email and storage. TEST and LIVE activation are blocked until adapters and verified credentials exist." testId="settings-integrations-card">
              <div className="border rounded-xl overflow-hidden">
                <Table>
                  <TableHeader className="bg-secondary/60"><TableRow><TableHead className={thCls}>Integration</TableHead><TableHead className={thCls}>Mode</TableHead><TableHead className={thCls}>Provider</TableHead><TableHead className={thCls}>Notes</TableHead>{canInt && <TableHead className={thCls} />}</TableRow></TableHeader>
                  <TableBody>
                    {integrations.items.map((i) => (
                      <TableRow key={i.key} className={rowCls} data-testid="integration-row">
                        <TableCell className={tdCls}><p className="font-medium">{i.name}</p><p className="text-[11px] text-muted-foreground font-mono">{i.key}</p></TableCell>
                        <TableCell className={tdCls}><StatusBadge status={i.mode} label={i.mode === "NOT_CONFIGURED" ? "Not configured" : i.mode} />{i.last_check && !i.last_check.ok && <span className="block text-[10px] text-destructive mt-1">{i.last_check.error}</span>}</TableCell>
                        <TableCell className={`${tdCls} text-muted-foreground`}>{i.provider || "—"}</TableCell>
                        <TableCell className={`${tdCls} text-xs text-muted-foreground max-w-[260px]`}>{i.notes || "—"}{i.updated_at && <span className="block text-[10px]">Updated {dateTime(i.updated_at)} {i.updated_by ? `by ${i.updated_by}` : ""}</span>}</TableCell>
                        {canInt && <TableCell className={`${tdCls} text-right`}>{i.key !== "image_storage" && <Button variant="ghost" size="sm" onClick={() => { setIntForm({ provider: i.provider || "", notes: i.notes || "" }); setEditInt(i); }} data-testid="integration-edit-button">Edit</Button>}</TableCell>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          )}
        </div>
        <div className="space-y-6">
          <SectionCard title="Platform" testId="settings-platform-card">
            <KeyValue items={[["Schema version", data.schema_version], ["Base currency", "EUR"], ["Money precision", "Decimal128 · 2 dp"], ["DB transactions", data.txn_supported ? "Supported" : "Guarded single-doc updates"], ["Checkout", "Phase 4"], ["Payments", "Not configured"]]} />
          </SectionCard>
          <SectionCard title="Go-live checklist" description="What must be true before LIVE mode is possible.">
            <ul className="text-sm space-y-2 list-disc pl-4 text-muted-foreground">
              <li>Verified payment provider credentials and webhooks</li>
              <li>Merchant and currency eligibility confirmed</li>
              <li>Successful authorised live-mode test transactions</li>
              <li>Legal entity country and tax configuration</li>
              <li>Production object storage with backups</li>
            </ul>
          </SectionCard>
        </div>
      </div>

      <Dialog open={!!editInt} onOpenChange={(o) => !o && setEditInt(null)}>
        <DialogContent data-testid="integration-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl font-medium">{editInt?.name}</DialogTitle><DialogDescription>Record the intended provider and notes. Mode stays NOT CONFIGURED until an adapter with verified credentials is delivered.</DialogDescription></DialogHeader>
          <form onSubmit={saveInt} className="space-y-4 pt-2">
            <Field label="Intended provider" htmlFor="int-prov"><Input id="int-prov" value={intForm.provider} onChange={(e) => setIntForm({ ...intForm, provider: e.target.value })} placeholder="e.g. Stripe" data-testid="integration-provider-input" /></Field>
            <Field label="Notes" htmlFor="int-notes"><Textarea id="int-notes" rows={3} value={intForm.notes} onChange={(e) => setIntForm({ ...intForm, notes: e.target.value })} data-testid="integration-notes-input" /></Field>
            {editInt?.required_for_live && <div className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground"><p className="font-medium text-foreground mb-1">Required for LIVE</p><ul className="list-disc pl-4 space-y-0.5">{(Array.isArray(editInt.required_for_live) ? editInt.required_for_live : [String(editInt.required_for_live)]).map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setEditInt(null)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="integration-save-button">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
